import { useCallback } from 'react';
import { useApolloClient } from '@apollo/client';
import { storeKeyMapper } from '@shared/utils';
import type { Cart } from '../codegen/types';
import { useCartContext } from '../context/cartContext';
import { useLocaleContext } from '../context/localeContext';
import { GetCartDocument } from '../graphqlDocument/cart/getCart.generated';
import type { GetCartQuery } from '../graphqlDocument/cart/getCart.generated';
import { getCtLocale } from '../utils';
import { useNRBASession } from './useNRBASession';

export const useCartValidation = () => {
  const { cartId, anonymousId, miniCart, setMiniCart } = useCartContext();
  const { locale } = useLocaleContext();
  const nrbaContextHeader = useNRBASession();
  const apolloClient = useApolloClient();

  const checkForDiscountsError = (latestCart: Cart | null): boolean => {
    if (!latestCart) return false;
    return latestCart.errors?.some((error) => error.__typename === 'DiscountCodeError') ?? false;
  };

  const validateDiscounts = useCallback(async () => {
    try {
      const { data } = await apolloClient.query<GetCartQuery>({
        query: GetCartDocument,
        variables: {
          input: {
            anonymousId,
            cartId,
            locale: getCtLocale(locale),
            storeKey: storeKeyMapper(locale),
            recalculate: true,
          },
        },
        ...nrbaContextHeader,
        fetchPolicy: 'network-only',
      });

      const latestCart = data.getCart as Cart;
      const hasDiscountError = checkForDiscountsError(latestCart);
      return { hasDiscountError, latestCart };
    } catch (err) {
      console.error('Error validating discounts:', err);
      return { hasDiscountError: false, latestCart: null };
    }
  }, [anonymousId, apolloClient, cartId, locale, nrbaContextHeader]);

  const validateDiscountsBeforePayment = useCallback(async () => {
    const hasDiscounts = (miniCart?.discountCodes?.length ?? 0) > 0;
    if (!hasDiscounts) return true;

    const { hasDiscountError, latestCart } = await validateDiscounts();
    if (hasDiscountError && latestCart) {
      // This will trigger the DiscountExpiredModal
      setMiniCart(latestCart);
      return false;
    }
    return true;
  }, [miniCart, validateDiscounts, setMiniCart]);

  return { validateDiscounts, validateDiscountsBeforePayment };
};
